import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserSquare2, Plus, Search, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ContactButtons } from "@/components/ContactButtons";
import { toast } from "sonner";

const SEGMENTS = ["hot", "warm", "cold", "renewal", "multi_policy", "high_value", "inactive", "referral"];

export default function Customers() {
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [segment, setSegment] = useState("all");
  const [users, setUsers] = useState([]);

  const params = { ...(q ? { q } : {}), ...(segment !== "all" ? { segment } : {}) };
  const { items, total, loading, page, setPage, refetch } = useList("/customers", params);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const staff = users.filter((u) => ["telecaller", "team_leader", "renewal_executive", "sales_manager"].includes(u.role));

  const create = async (v) => {
    try {
      await api.post("/customers", v); refetch();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.duplicates) {
        if (window.confirm(`Possible duplicate found: ${d.duplicates.map((x) => x.name).join(", ")}. Create anyway?`)) {
          await api.post("/customers", { ...v, force: true }); refetch(); return;
        }
        throw new Error("Cancelled — possible duplicate");
      }
      throw e;
    }
  };

  const fields = [
    { name: "name", label: "Full Name", required: true },
    { name: "primary_mobile", label: "Primary Mobile", required: true },
    { name: "alternate_mobile", label: "Alternate Mobile" },
    { name: "email", label: "Email", type: "email" },
    { name: "whatsapp_number", label: "WhatsApp Number" },
    { name: "dob", label: "Date of Birth", type: "date" },
    { name: "occupation", label: "Occupation" },
    { name: "city", label: "City" }, { name: "state", label: "State" }, { name: "pincode", label: "Pincode" },
    { name: "address", label: "Address", type: "textarea", full: true },
    { name: "segment", label: "Segment", type: "select", options: SEGMENTS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "assigned_to", label: "Assign To", type: "select", options: staff.map((u) => ({ value: u.id, label: u.name })) },
  ];

  const columns = [
    { key: "customer_id", header: "Customer ID", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.customer_id}</span> },
    { key: "name", header: "Name", render: (r) => <div className="font-semibold">{r.name}</div> },
    { key: "primary_mobile", header: "Mobile", render: (r) => <span className="font-mono text-xs">{r.primary_mobile}</span> },
    { key: "city", header: "Location", render: (r) => <span className="text-sm">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</span> },
    { key: "segment", header: "Segment", render: (r) => <StatusBadge value={r.segment} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "actions", header: "Actions", render: (r) => (
      <ContactButtons name={r.name} mobile={r.primary_mobile} testPrefix="customer-row" track={{ customer_id: r.id, context: "customer" }} />
    ) },
  ];

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${total} customers · Customer 360 central records`} icon={UserSquare2}
        actions={hasPerm("customers:add") && (
          <FormDialog title="New Customer" description="Duplicate mobile/email is detected automatically." fields={fields} onSubmit={create} submitLabel="Create Customer" testId="create-customer-dialog"
            trigger={<Button data-testid="create-customer-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Customer</Button>} />
        )} />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, mobile, customer ID, email..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input" />
        </div>
        <Select value={segment} onValueChange={setSegment}><SelectTrigger className="w-44" data-testid="customer-segment-filter"><SelectValue placeholder="Segment" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Segments</SelectItem>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>

      <DataTable columns={columns} rows={items} loading={loading} onRowClick={(r) => nav(`/customers/${r.id}`)} rowTestId="customer-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
